import { fetchActiveSosApi } from "api/sos";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNotificationContext } from "./NotificationContext";
import { getRole } from "helpers/privilege";
import { fetchSearchRangeSettingApi } from "api/setting";
import { ISearchRange } from "models/setting";
import { useLocation } from "react-router-dom";

interface IDataLoading {
  sosCount: number;
  searchRange: ISearchRange;
  reloadAccount: number;
  reloadAppVersion: number;
  reloadCity: number;
  reloadDriver: number;
  reloadFaq: number;
  reloadFleet: number;
  reloadFleetModel: number;
  reloadNews: number;
  reloadOtp: number;
  reloadPassenger: number;
  reloadPlace: number;
  reloadReportRequest: number;
  reloadSos: number;
  reloadTrip: number;
  reloadTripRequest: number;
  reloadWallet: number;
}

interface IDataLoadingProps {
  searchRange: ISearchRange;
  sosCount: number;
  loadAccounts: () => void;
  reloadAccount: number;
  loadAppVersions: () => void;
  reloadAppVersion: number;
  loadCities: () => void;
  reloadCity: number;
  loadDrivers: () => void;
  reloadDriver: number;
  loadFaqs: () => void;
  reloadFaq: number;
  loadFleets: () => void;
  reloadFleet: number;
  loadFleetModels: () => void;
  reloadFleetModel: number;
  loadNewses: () => void;
  reloadNews: number;
  loadOtp: () => void;
  reloadOtp: number;
  loadPassengers: () => void;
  reloadPassenger: number;
  loadPlaces: () => void;
  reloadPlace: number;
  loadReportRequests: () => void;
  reloadReportRequest: number;
  loadSos: () => void;
  reloadSos: number;
  loadTrips: () => void;
  reloadTrip: number;
  loadTripRequests: () => void;
  reloadTripRequest: number;
  loadWallets: () => void;
  reloadWallet: number;
}

export const DataLoadingContext = createContext<IDataLoadingProps>(
  {} as IDataLoadingProps
);

export const useDataLoadingContext = () =>
  useContext<IDataLoadingProps>(DataLoadingContext);

export function DataLoadingContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { showWarning } = useNotificationContext();
  const location = useLocation();
  const [reload, setReload] = useState<IDataLoading>({
    searchRange: {
      latitude: "13.496494229233356",
      longitude: "39.47494624044569",
      radius: "150000",
    },
    sosCount: 0,
    reloadAccount: 0,
    reloadAppVersion: 0,
    reloadCity: 0,
    reloadDriver: 0,
    reloadFaq: 0,
    reloadFleet: 0,
    reloadFleetModel: 0,
    reloadNews: 0,
    reloadOtp: 0,
    reloadPassenger: 0,
    reloadPlace: 0,
    reloadReportRequest: 0,
    reloadSos: 0,
    reloadTrip: 0,
    reloadTripRequest: 0,
    reloadWallet: 0,
  });

  useEffect(() => {
    fetchSos(true);
    fetchSearchRadius();
  }, [location.pathname]); // eslint-disable-line

  const loadAccounts = () =>
    setReload((prev) => ({ ...prev, reloadAccount: prev.reloadAccount + 1 }));
  const loadAppVersions = () =>
    setReload((prev) => ({
      ...prev,
      reloadAppVersion: prev.reloadAppVersion + 1,
    }));
  const loadCities = () =>
    setReload((prev) => ({
      ...prev,
      reloadCity: prev.reloadCity + 1,
    }));
  const loadDrivers = () =>
    setReload((prev) => ({
      ...prev,
      reloadDriver: prev.reloadDriver + 1,
    }));
  const loadFaqs = () =>
    setReload((prev) => ({
      ...prev,
      reloadFaq: prev.reloadFaq + 1,
    }));
  const loadFleets = () =>
    setReload((prev) => ({
      ...prev,
      reloadFleet: prev.reloadFleet + 1,
    }));
  const loadFleetModels = () =>
    setReload((prev) => ({
      ...prev,
      reloadFleetModel: prev.reloadFleetModel + 1,
    }));
  const loadOtp = () =>
    setReload((prev) => ({
      ...prev,
      reloadOtp: prev.reloadOtp + 1,
    }));
  const loadNewses = () =>
    setReload((prev) => ({
      ...prev,
      reloadNews: prev.reloadNews + 1,
    }));
  const loadPassengers = () =>
    setReload((prev) => ({
      ...prev,
      reloadPassenger: prev.reloadPassenger + 1,
    }));
  const loadPlaces = () =>
    setReload((prev) => ({
      ...prev,
      reloadPlace: prev.reloadPlace + 1,
    }));
  const loadReportRequests = () =>
    setReload((prev) => ({
      ...prev,
      reloadReportRequest: prev.reloadReportRequest + 1,
    }));
  const loadSos = async () => {
    console.log("🔄 loadSos called - reloading SOS list");
    // First increment the reload counter to trigger SOS list refresh
    setReload((prev) => ({
      ...prev,
      reloadSos: prev.reloadSos + 1,
    }));
    // Then fetch and update the active SOS count
    await fetchSos(true); // Pass true to show warning
    showWarning("New SOS request received");
  };

  const loadTrips = () =>
    setReload((prev) => ({
      ...prev,
      reloadTrip: prev.reloadTrip + 1,
    }));
  const loadTripRequests = () =>
    setReload((prev) => ({
      ...prev,
      reloadTripRequest: prev.reloadTripRequest + 1,
    }));
  const loadWallets = () =>
    setReload((prev) => ({
      ...prev,
      reloadWallet: prev.reloadWallet + 1,
    }));

  const fetchSos = async (_showWarning?: boolean) => {
    const role = getRole();
    if (role !== null && role !== "police" && role !== "casher") {
      const { data: response } = await fetchActiveSosApi();
      if (response) {
        setReload((prev) => ({
          ...prev,
          sosCount: response.data,
        }));
        if (_showWarning) {
          for (let i = 0; i < response.data; i++) {
            showWarning("New SOS request received");
          }
        }
      }
    }
  };

  const fetchSearchRadius = async () => {
    const role = getRole();
    if (role !== null && role !== "police" && role !== "casher") {
      const { data: response } = await fetchSearchRangeSettingApi();
      if (response) {
        setReload((prev) => ({
          ...prev,
          searchRange: response.data.value as ISearchRange,
        }));
      }
    }
  };

  return (
    <DataLoadingContext.Provider
      value={{
        ...reload,
        loadAccounts,
        loadAppVersions,
        loadCities,
        loadDrivers,
        loadFaqs,
        loadFleets,
        loadFleetModels,
        loadOtp,
        loadNewses,
        loadPassengers,
        loadPlaces,
        loadReportRequests,
        loadSos,
        loadTrips,
        loadTripRequests,
        loadWallets,
      }}
    >
      {children}
    </DataLoadingContext.Provider>
  );
}
